/**
 * Demokratiuka brand tokens.
 * Basert på profilhåndbok (Nosleeptillbrooklyn 2026).
 *
 * Paletten er navngitt etter verdier. Ytringsfrihet (#485cf0) er primærfarge.
 * Hver kampanje tildeles en tematisk fargetone.
 */

export const DEMOKRATIUKA_COLORS = {
  ytringsfrihet: '#485cf0',   // Primary — Pantone 2728 C
  stabilitet: '#2b1e6e',
  tillit: '#5fafa1',
  trygghet: '#0e3a33',
  mangfold: '#FBF6C2',
  rettferdighet: '#ef6f64',
  samtale: '#480052',
  fremtid: '#7a69d6',
} as const;

export type ThemeColor = keyof typeof DEMOKRATIUKA_COLORS;

/**
 * Kontrast-tekstfarge for hver bakgrunn (WCAG AA+).
 */
export const DEMOKRATIUKA_INK: Record<ThemeColor, string> = {
  ytringsfrihet: '#ffffff',
  stabilitet: '#ffffff',
  tillit: '#0e3a33',
  trygghet: '#ffffff',
  mangfold: '#2b1e6e',
  rettferdighet: '#ffffff',
  samtale: '#ffffff',
  fremtid: '#ffffff',
} as const;

/**
 * Sekundær/aksent-farge per tema.
 */
export const DEMOKRATIUKA_ACCENT: Record<ThemeColor, string> = {
  ytringsfrihet: '#ffffff',
  stabilitet: '#7a69d6',
  tillit: '#ffffff',
  trygghet: '#5fafa1',
  mangfold: '#ef6f64',
  rettferdighet: '#ffffff',
  samtale: '#ef6f64',
  fremtid: '#ffffff',
} as const;

/**
 * Font-stack.
 */
export const DEMOKRATIUKA_FONT =
  '"New Hero", "Be Vietnam Pro", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

/**
 * 4 aktive plakat-kampanjer. Hvert spørsmål henges opp på alle lokasjonene
 * (se DEMOKRATIUKA_LOCATIONS) — lokasjonen spores via ?loc=-query på QR-lenken.
 */
export interface Campaign {
  slug: string;
  title: string;
  color: ThemeColor;
  subtitle?: string;
}

export const DEMOKRATIUKA_CAMPAIGNS: Record<string, Campaign> = {
  'jesus-abort-skeive': {
    slug: 'jesus-abort-skeive',
    title: 'Er det mulig å være glad i Jesus og samtidig støtte abort og skeives rettigheter?',
    color: 'rettferdighet',
  },
  'milliardaerer-bidrar': {
    slug: 'milliardaerer-bidrar',
    title: 'Milliardærer blir rikere hvert år. Bidrar de nok tilbake?',
    color: 'mangfold',
  },
  'monarkiet-udemokratisk': {
    slug: 'monarkiet-udemokratisk',
    title: 'Monarkiet er udemokratisk. Hvorfor elsker vi det?',
    color: 'samtale',
  },
  'bistand-norge-forst': {
    slug: 'bistand-norge-forst',
    title: 'Vi gir milliarder i bistand hvert år. Bør vi fortsette? Eller prioritere Norge først?',
    color: 'fremtid',
  },
  'danby-choi-sporsmal': {
    slug: 'danby-choi-sporsmal',
    title: 'Hva ønsker du og si til Danby Choi?',
    color: 'tillit',
    subtitle: 'Lørdag 2. mai · 19:00 · Teateret',
  },
  'alexandra-fredwall-sporsmal': {
    slug: 'alexandra-fredwall-sporsmal',
    title: 'Skriv til Alexandra Fredwall.',
    color: 'rettferdighet',
    subtitle: 'Lederen i Rød Ungdom møter publikum',
  },
  'abort-mission': {
    slug: 'abort-mission',
    title: 'Abort Mission — hva mener du?',
    color: 'rettferdighet',
    subtitle: 'Debatt med Danby Choi og Alexandra Joy · 14:30–15:00',
  },
  'kulturdebatt-kulturministeren': {
    slug: 'kulturdebatt-kulturministeren',
    title: 'Kulturdebatt med kulturministeren',
    color: 'ytringsfrihet',
    subtitle: 'Lubna Jaffery · 4. mai · 10:15 · Teateret',
  },
  'steinfeld-russland': {
    slug: 'steinfeld-russland',
    title: 'Russland — Vår dystre nåtid',
    color: 'stabilitet',
    subtitle: 'En kveld med Hans-Wilhelm Steinfeld · 6. mai · 19:00 · Hovedscenen',
  },
};

/**
 * 7 lokasjoner hvor plakatene henges opp. Hver lokasjon har sin egen
 * QR-kode per kampanje slik at svar kan tilskrives lokasjonen.
 */
export interface Location {
  slug: string;
  label: string;
}

export const DEMOKRATIUKA_LOCATIONS: Location[] = [
  { slug: 'kkg',          label: 'KKG' },
  { slug: 'uia',          label: 'UiA' },
  { slug: 'kvadraturen',  label: 'Kvadraturen VGS' },
  { slug: 'vagsbygd',     label: 'Vågsbygd VGS' },
  { slug: 'tangen',       label: 'Tangen VGS' },
  { slug: 'skippergata',  label: 'Skippergata / Markens' },
  { slug: 'fiskebrygga',  label: 'Fiskebrygga' },
];

export function getLocationLabel(slug: string | null | undefined): string {
  if (!slug) return 'Ukjent';
  return DEMOKRATIUKA_LOCATIONS.find(l => l.slug === slug)?.label ?? slug;
}

/**
 * Hjelpefunksjon — henter farge-objekt for en kampanje.
 */
export function getCampaignTheme(slug: string | undefined) {
  const campaign = slug ? DEMOKRATIUKA_CAMPAIGNS[slug] : undefined;
  const color: ThemeColor = campaign?.color ?? 'ytringsfrihet';
  return {
    campaign,
    color,
    bg: DEMOKRATIUKA_COLORS[color],
    ink: DEMOKRATIUKA_INK[color],
    accent: DEMOKRATIUKA_ACCENT[color],
  };
}
